import React from 'react';
import {
	Chip,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tooltip,
	Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EdButton from '@Components/UI/Buttons/EdButton/EdButton';
import { FlexLayout } from '@Styled/utilities';
import styled from 'styled-components';

export type EducationSectionRow = {
	id: number;
	name: string;
	chips?: string[];
};

type Props = {
	title: string;
	chipsHeader?: string;
	rows: EducationSectionRow[];
	onAdd: () => void;
	onEdit: (row: EducationSectionRow) => void;
	onDelete: (row: EducationSectionRow) => void;
};

const EducationSection: React.FC<Props> = ({
	title,
	chipsHeader,
	rows,
	onAdd,
	onEdit,
	onDelete,
}) => {
	return (
		<SectionCard>
			<SectionHeader justifyContent="space-between" alignItems="center">
				<Typography variant="h6">{title}</Typography>
				<EdButton
					variant="contained"
					edcolor="purple"
					startIcon={<AddIcon />}
					onClick={onAdd}
				>
					Add
				</EdButton>
			</SectionHeader>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>NAME</TableCell>
						{chipsHeader && <TableCell>{chipsHeader}</TableCell>}
						<TableCell align="right">ACTIONS</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{rows.length === 0 && (
						<TableRow>
							<TableCell colSpan={chipsHeader ? 3 : 2}>
								<Typography variant="body2" color="textSecondary">
									No entries yet
								</Typography>
							</TableCell>
						</TableRow>
					)}
					{rows.map((row) => (
						<TableRow key={row.id}>
							<TableCell>{row.name}</TableCell>
							{chipsHeader && (
								<TableCell>
									{row.chips && row.chips.length > 0 ? (
										row.chips.map((chip) => (
											<LinkedChip
												key={`${row.id}-${chip}`}
												size="small"
												variant="outlined"
												color="primary"
												label={chip}
											/>
										))
									) : (
										<Typography variant="caption" color="textSecondary">
											Not linked
										</Typography>
									)}
								</TableCell>
							)}
							<TableCell align="right">
								<Tooltip title="Edit">
									<IconButton size="small" onClick={() => onEdit(row)}>
										<EditIcon fontSize="small" />
									</IconButton>
								</Tooltip>
								<Tooltip title="Delete">
									<IconButton size="small" onClick={() => onDelete(row)}>
										<DeleteIcon fontSize="small" />
									</IconButton>
								</Tooltip>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</SectionCard>
	);
};

export default EducationSection;

const SectionCard = styled(Paper)`
	padding: 1rem 1.5rem;
`;

const SectionHeader = styled(FlexLayout)`
	margin-bottom: 0.5rem;
`;

const LinkedChip = styled(Chip)`
	margin: 0.125rem 0.25rem 0.125rem 0;
`;
